import { authClient } from "@client/lib/auth-client";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";

/**
 * Wipes the React Query cache whenever the signed-in user changes.
 *
 * Every tRPC response is scoped to the session on the server, but the cache key
 * is not — `["sites","list"]` is the same key for every account. Without this,
 * signing out and signing in as someone else (in the same tab, no reload) shows
 * the previous account's cached sites/credits until each query refetches.
 *
 * Mounted once at the router root so it covers every sign-out path (user menu,
 * session expiry, OAuth callback), not just the logout button.
 */
export function useAuthCacheGuard() {
	const { data: session, isPending } = authClient.useSession();
	const queryClient = useQueryClient();
	// `undefined` = we haven't recorded a user yet (first resolve after mount)
	const lastUserId = useRef<string | null | undefined>(undefined);

	const userId = session?.user?.id ?? null;

	useEffect(() => {
		// Wait until the session resolves; `isPending` means we don't know yet.
		if (isPending) return;

		if (lastUserId.current === undefined) {
			lastUserId.current = userId;
			return;
		}

		if (lastUserId.current !== userId) {
			lastUserId.current = userId;
			// Remove cached data *and* cancel in-flight queries from the old session.
			queryClient.clear();
		}
	}, [userId, isPending, queryClient]);
}
